// packages/ui-kit/src/primitives/Checkbox.tsx
// C3F v18 — reference: design/system/components/forms/Checkbox.{jsx,prompt.md}
// The most repeated gesture of the product: 44px target, 22px box.
import { type InputHTMLAttributes, type ReactNode, forwardRef } from "react";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Inline label rendered next to the box. */
  label?: ReactNode;
  /** Secondary line under the label (muted caption). */
  description?: ReactNode;
}

/**
 * Shared checkbox — a system-drawn 22px box over a visually-hidden native
 * input (accent fill + drawn check when on). Pass `label`/`description` for
 * the common row pattern, or use it bare inside a custom layout.
 */
export const Checkbox = forwardRef<HTMLInputElement, Props>(function Checkbox(
  { label, description, className = "", disabled, ...rest },
  ref,
) {
  const box = (
    <span className="relative grid h-[22px] w-[22px] shrink-0 place-items-center">
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className={`peer absolute inset-0 m-0 h-[22px] w-[22px] cursor-[inherit] opacity-0 ${className}`}
        {...rest}
      />
      <span
        aria-hidden
        className="pointer-events-none h-[22px] w-[22px] rounded-xs border border-border-control bg-elevated transition-colors duration-fast peer-checked:border-transparent peer-checked:bg-accent peer-focus-visible:shadow-focus peer-disabled:opacity-45"
      />
      {/* Drawn check (CSS borders, per DS reference — no SVG). */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-2.5 w-1.5 -translate-x-1/2 -translate-y-[60%] rotate-45 border-b-2 border-r-2 border-accent-fg opacity-0 peer-checked:opacity-100"
      />
    </span>
  );
  if (label == null && description == null) return box;
  return (
    <label
      className={`flex min-h-touch gap-3 ${description ? "items-start" : "items-center"} ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <span className={description ? "mt-0.5" : ""}>{box}</span>
      {/* opacity here is the disabled-control exception, not text dimming */}
      <span className={`min-w-0 ${disabled ? "opacity-45" : ""}`}>
        {label != null && <span className="text-sm font-medium text-fg-default">{label}</span>}
        {description != null && (
          <span className="mt-0.5 block text-caption text-fg-muted">{description}</span>
        )}
      </span>
    </label>
  );
});
